import { FC, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Typography, Tooltip, useTheme } from '@mui/material';
import { observer } from 'mobx-react';

import WarningAmberIcon from '../icons/WarningAmberIcon';
import CoreVirtualizedTable from '../ui/core/CoreVirtualizedTable';
import { ActionType, Claim, CLAIMS_COLUMN_FILTER_TYPE, CLAIMS_TABLE_COLUMN_OPTIONS, ClaimsTableColumnId } from '../../types/claims';
import { useClaimsStore } from '../../hooks/useClaimsStore';
import AssignedUserSelect from './components/AssignedUserSelect';
import ClaimsQueueSummary from './ClaimsQueueSummary';
import { formatCurrency, formatDisplayDate, formatInstant } from '../helpers/dataHelper';
import StatusChip from './components/table-components/StatusChip';
import { getDataWarningMessages } from './claimDataWarnings';
import { ACTION_TYPE_LABELS } from './components/edi-components/claimAppeals';
import {
    ColumnFiltersConfig,
    ColumnFilterState,
    FilterOperator,
    SimpleColumnDef,
    SortingState,
} from '../../types/table';
import { useChatStore } from '../../hooks/useChatStore';
import { useMainStore } from '../../hooks/useMainStore';
import { loadPersistedClaimFilter } from '../helpers/claimFilterStorage';

interface ClaimsTableProps {
    selectTableRowCallback: (claimId: string | null) => void;
}

const formatActionTypes = (types?: ActionType[] | null) =>
    types?.length
        ? Array.from(new Set(types)).map((type) => ACTION_TYPE_LABELS[type] ?? type).join(', ')
        : '-';

const ClaimsTable: FC<ClaimsTableProps> = observer(({ selectTableRowCallback }) => {
    const theme = useTheme()

    const {
        claimsData,
        isClaimsDataLoading,
        loadClaimsData,
        loadMoreClaims,
        activeTab,
        selectedQuickFilter,
        hasMoreClaims,
        isLoadingMore,
        orderBy,
        allFilters,
        assignedUsers,
        setOrderBy,
        setClaimToPreview,
        claimToPreview,
        claimKeywordSearch,
        setColumnFilter,
        clearColumnFilter,
        getUniqueColumnValues,
        getColumnFilterValues,
        getColumnFilterOperator,
        columnSettings,
        isColumnRequired,
        toggleRequiredColumn,
        visibleColumnIds,
    } = useClaimsStore();
    const chatStore = useChatStore();
    const { filtersScope } = useMainStore();

    // Convert store orderBy to SortingState for the table
    const sorting: SortingState = useMemo(() => {
        if (!orderBy) return [];
        return [{ id: orderBy.field, desc: orderBy.direction === 'desc' }];
    }, [orderBy]);
    const [tableHeight, setTableHeight] = useState<number>(500);
    const containerRef = useRef<HTMLDivElement>(null);

    // Calculate table height to fit the page
    const calculateHeight = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const bottomPadding = 12; // Padding from bottom of page
            const calculatedHeight = viewportHeight - rect.top - bottomPadding;
            setTableHeight(Math.max(300, calculatedHeight)); // Minimum height of 300px
        }
    }, []);

    useEffect(() => {
        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        return () => window.removeEventListener('resize', calculateHeight);
    }, [calculateHeight]);

    // calculateHeight measures our distance from the top of the viewport, so
    // anything laid out above us changes the answer whenever it changes height
    // -- the summary bar mounting, swapping its spinner for the taller metrics
    // card, or its caption wrapping when the claim preview narrows the column.
    // None of those fire `resize`, and the stale height leaves the table
    // overhanging the viewport. So watch the siblings above us directly.
    //
    // We observe them rather than ourselves: our own height IS the value being
    // set here, so observing it would feed back into itself (REV-2507).
    useEffect(() => {
        const container = containerRef.current;
        const parent = container?.parentElement;
        if (!parent || typeof ResizeObserver === 'undefined') return;

        const sizeObserver = new ResizeObserver(() => calculateHeight());

        const observeElementsAbove = () => {
            sizeObserver.disconnect();
            for (const child of Array.from(parent.children)) {
                if (child === container) break;
                sizeObserver.observe(child);
            }
            calculateHeight();
        };

        observeElementsAbove();

        // The summary bar renders nothing until it has totals, so the set of
        // siblings above us changes at runtime and has to be re-attached.
        const treeObserver = new MutationObserver(observeElementsAbove);
        treeObserver.observe(parent, { childList: true });

        return () => {
            sizeObserver.disconnect();
            treeObserver.disconnect();
        };
    }, [calculateHeight]);

    const { showMissingClaims } = useClaimsStore();

    useEffect(() => {
        loadClaimsData()
    }, [activeTab, selectedQuickFilter, orderBy, allFilters, showMissingClaims, claimKeywordSearch]);

    // Columns that should have filter popover
    const filterableColumns = useMemo(() => [
        'claimNumber',
        'patientName',
        'medicalRecordNumber',
        'dateOfService',
        'dob',
        'payer',
        'procedureCode',
        'diagnosisCode',
        'billedAmount',
        'paidAmount',
        'patientBalance',
        'status',
        'claimType',
        'denialReason',
        'assignedUser',
        'placeOfService',
        'dateSubmitted',
        'appealLetter',
        'appealed',
        'actionType',
        'linked',
        'paidInclResubmission',
        'disposition',
        'updatedAt',
        'createdAt',
    ], []);

    const columnFilterTypes = CLAIMS_COLUMN_FILTER_TYPE;

    // Build filters object from allFilters for column filter UI (operator + values)
    const columnFiltersFromAllFilters = useMemo(() => {
        const filters: Record<string, ColumnFilterState> = {};

        filterableColumns.forEach(columnId => {
            const values = getColumnFilterValues(columnId);
            const operator = getColumnFilterOperator(columnId);
            
            filters[columnId] = { operator, values };
        });
        return filters;
    }, [allFilters, assignedUsers, filterableColumns, getColumnFilterValues, getColumnFilterOperator]);

    // Column filters configuration
    const columnFiltersConfig: ColumnFiltersConfig = useMemo(() => ({
        filters: columnFiltersFromAllFilters,
        getOptions: getUniqueColumnValues,
        onApply: (columnId: string, values: string[], operator?: FilterOperator) =>
            setColumnFilter(columnId, values, operator),
        onClear: clearColumnFilter,
        filterableColumns,
        columnFilterTypes,
        isColumnPinned: isColumnRequired,
        onTogglePin: (columnId: string) => toggleRequiredColumn(columnId as ClaimsTableColumnId),
        canPinColumn: (columnId: string) => !CLAIMS_TABLE_COLUMN_OPTIONS.find(col => col.id === columnId)?.mandatory,
    }), [columnFiltersFromAllFilters, getUniqueColumnValues, setColumnFilter, clearColumnFilter, filterableColumns, columnFilterTypes, isColumnRequired, toggleRequiredColumn, columnSettings]);

    // Handle sorting change from the table
    const handleSortingChange = useCallback((newSorting: SortingState) => {
        if (newSorting.length === 0) {
            setOrderBy(null);
        } else {
            const { id, desc } = newSorting[0];
            setOrderBy({ field: id, direction: desc ? 'desc' : 'asc' });
        }
    }, [setOrderBy]);

    const columns: SimpleColumnDef<Claim>[] = useMemo(() => [
        {
            id: 'warnings',
            header: '',
            width: 35,
            minWidth: 35,
            maxWidth: 35,
            accessorKey: 'dataWarnings',
            enableSorting: false,
            cell: (value) => {
                const warnings = getDataWarningMessages(value);
                if (!warnings.length) return null;
                return (
                    <Tooltip title={warnings.join('\n')} arrow>
                        <span style={{ display: 'inline-flex', width: '100%', justifyContent: 'center' }}>
                            <WarningAmberIcon color="#D97706" size={20} />
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            id: 'claimNumber',
            header: 'Claim #',
            accessorKey: 'claimNumber',
            width: 140,
            minWidth: 140,
            cell: (value) => (
                <Typography
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    {value}
                </Typography>
            ),
        },
        {
            id: 'claimType',
            header: 'Type',
            accessorKey: 'claimType',
            width: 100,
            minWidth: 100,
            cell: (value) => (
                <Typography
                    sx={{
                        fontWeight: 500,
                        textAlign: 'center',
                    }}
                >
                    {value || '-'}
                </Typography>
            ),
        },
        {
            id: 'patientName',
            header: 'Patient',
            accessorKey: 'patientName',
            width: 140,
            minWidth: 120,
            cell: (value) => (
                <Typography>
                    {value}
                </Typography>
            ),
        },
        {
            id: 'medicalRecordNumber',
            header: 'MRN',
            accessorKey: 'medicalRecordNumber',
            width: 140,
            minWidth: 140,
            cell: (value) => (
                <Typography>
                    {value || '-'}
                </Typography>
            ),
        },
        {
            id: 'dob',
            header: 'DOB',
            accessorKey: 'dob',
            width: 100,
            minWidth: 100,
            cell: (value) => (
                <Typography>
                    {formatDisplayDate(value)}
                </Typography>)
        },
        {
            id: 'dateOfService',
            header: 'DOS',
            accessorKey: 'dateOfService',
            width: 100,
            minWidth: 100,
            cell: (value) => (
                <Typography>
                    {formatDisplayDate(value)}
                </Typography>
            ),
        },
        {
            id: 'placeOfService',
            header: 'POS',
            accessorKey: 'placeOfService',
            width: 70,
            minWidth: 70,
            cell: (value) => {
                if (!value) return <Typography>-</Typography>;
                const code = value.split(/[-–—\s]/)[0]?.trim() || value;
                return (
                    <Tooltip title={value} arrow>
                        <Typography sx={{ cursor: 'default' }}>
                            {code}
                        </Typography>
                    </Tooltip>
                );
            },
        },
        {
            id: 'procedureCode',
            header: 'Procedure',
            accessorKey: 'procedureCode',
            width: 100,
            minWidth: 100,
            // procedureCode is the claim's single principal procedure (ICD-10-PCS or
            // HCPCS, institutional claims only) -- never a comma-separated list, unlike
            // cptCode, so no truncation/tooltip needed here.
            cell: (value) => <Typography>{value || '-'}</Typography>,
        },
        {
            id: 'diagnosisCode',
            header: 'Diagnostic',
            accessorKey: 'diagnosisCode',
            width: 100,
            minWidth: 100,
            cell: (value) => {
                if (!value) return <Typography>-</Typography>;
                const codes = value.split(',').map((c: string) => c.trim()).filter(Boolean);
                if (codes.length <= 1) return <Typography>{codes.join(', ')}</Typography>;
                return (
                    <Tooltip title={codes.join(', ')} arrow>
                        <Typography sx={{ cursor: 'default' }}>
                            {`${codes.slice(0, 1).join(', ')} +${codes.length - 1}`}
                        </Typography>
                    </Tooltip>
                );
            },
        },
        {
            id: 'payer',
            header: 'Payer',
            accessorKey: 'payer',
            width: 220,
            minWidth: 110,
            cell: (value) => (
                <Typography>
                    {value}
                </Typography>
            ),
        },
        {
            id: 'billedAmount',
            header: 'Billed',
            accessorKey: 'billedAmount',
            width: 120,
            minWidth: 120,
            cell: (value) => (
                <Typography
                    sx={{
                        fontWeight: 500,
                        textAlign: 'right',
                    }}
                >
                    {formatCurrency(value)}
                </Typography>
            ),
        },
        {
            id: 'paidAmount',
            header: 'Paid',
            accessorKey: 'paidAmount',
            width: 120,
            minWidth: 120,
            cell: (value) => (
                <Typography
                    sx={{
                        fontWeight: 500,
                        textAlign: 'right',
                        color: value > 0 ? 'success.main' : 'text.secondary',
                    }}
                >
                    {formatCurrency(value)}
                </Typography>
            ),
        },
        {
            id: 'patientBalance',
            header: 'Patient Balance',
            accessorKey: 'patientBalance',
            width: 130,
            minWidth: 130,
            cell: (value) => (
                <Typography
                    sx={{
                        fontWeight: 500,
                        textAlign: 'right',
                        color: value > 0 ? '#D97706' : 'text.secondary',
                    }}
                >
                    {formatCurrency(value)}
                </Typography>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            accessorKey: 'status',
            width: 140,
            minWidth: 140,
            cell: (value) => <StatusChip status={value} fitContent />,
        },
        {
            id: 'denialReason',
            header: 'Denial Codes',
            accessorKey: 'denialReason',
            width: 290,
            minWidth: 290,
            cell: (value) => {
                const codes = value ? value.split(/[,;]\s*/).filter(Boolean) : [];
                
                return (
                    <Typography
                        title={value || ''}
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.4,
                        }}
                    >
                        {codes.map((code: string, idx: number) => (
                            <Typography
                                key={idx}
                                component="span"
                                sx={{
                                    fontSize: 'inherit',
                                    display: 'inline-block',
                                }}
                            >
                                {code.trim()}
                                {idx < codes.length - 1 && ', '}
                            </Typography>
                        ))}
                    </Typography>
                );
            },
        },
        {
            id: 'assignedUser',
            header: 'Assignee',
            accessorKey: 'assignedUser',
            width: 200,
            minWidth: 200,
            cell: (value, row) => (
                <AssignedUserSelect
                    claimId={row.id}
                    currentUser={value}
                />
            ),
        },
        {
            // A plain date off the 837, not an instant -- formatDisplayDate like
            // DOS/DOB, so it is not shifted into the viewer's timezone (REV-2507).
            id: 'dateSubmitted',
            header: 'Submitted',
            accessorKey: 'dateSubmitted',
            width: 120,
            minWidth: 120,
            cell: (value) => (
                <Typography>
                    {formatDisplayDate(value)}
                </Typography>
            ),
        },
        {
            id: 'appealLetter',
            header: 'Appeal letter',
            accessorKey: 'appealLetter',
            width: 130,
            minWidth: 130,
            cell: (value) => (
                <Typography>
                    {value ? 'Yes' : 'No'}
                </Typography>
            ),
        },
        {
            id: 'appealed',
            header: 'Appealed',
            accessorKey: 'appealed',
            width: 110,
            minWidth: 110,
            cell: (value) => (
                <Typography>
                    {value ? 'Yes' : 'No'}
                </Typography>
            ),
        },
        {
            // The recorded actions' types (REV-2628)
            id: 'actionType',
            header: 'Action type',
            accessorKey: 'actionTypes',
            width: 170,
            minWidth: 170,
            cell: (value) => (
                <Typography>
                    {formatActionTypes(value)}
                </Typography>
            ),
        },
        {
            // Linked to its correct-and-resubmit counterpart
            id: 'linked',
            header: 'Linked',
            accessorKey: 'linked',
            width: 110,
            minWidth: 110,
            cell: (value) => (
                <Typography>
                    {value ? 'Yes' : 'No'}
                </Typography>
            ),
        },
        {
            // The original's paid plus its linked corrected claim's (REV-2634);
            // null when the claim isn't the original of a live link
            id: 'paidInclResubmission',
            header: 'Paid incl. resubmission',
            accessorKey: 'paidInclResubmission',
            width: 190,
            minWidth: 190,
            cell: (value) => (
                <Typography
                    sx={{
                        fontWeight: 500,
                        textAlign: 'right',
                        color: Number(value) > 0 ? 'success.main' : 'text.secondary',
                    }}
                >
                    {value != null ? formatCurrency(Number(value)) : '-'}
                </Typography>
            ),
        },
        {
            id: 'disposition',
            header: 'Disposition',
            accessorKey: 'disposition',
            width: 150,
            minWidth: 150,
            cell: (value) => (
                <Typography>
                    {value || '-'}
                </Typography>
            ),
        },
        {
            id: 'createdAt',
            header: 'Imported',
            accessorKey: 'createdAt',
            width: 160,
            minWidth: 160,
            cell: (value) => (
                <Typography>
                    {value ? formatInstant(value, { dateOnly: true }) : '-'}
                </Typography>
            ),
        },
        {
            id: 'updatedAt',
            header: 'Last updated',
            accessorKey: 'updatedAt',
            width: 190,
            minWidth: 190,
            cell: (value) => (
                <Typography>
                    {value ? formatInstant(value, { dateOnly: true }) : '-'}
                </Typography>
            ),
        },
    ], []);

    // Visible columns in the store's display order (pinned first) -- the same
    // order the CSV export uses.
    const visibleColumns = useMemo(() => {
        const columnsById = new Map(columns.map(col => [col.id, col]));
        // The warnings icon column always stays first; it isn't part of the pin system
        // and relies on being first for its centered-icon styling.
        const warningsColumn = columns.filter(col => col.id === 'warnings');
        const rest = visibleColumnIds
            .map(id => columnsById.get(id))
            .filter((col): col is SimpleColumnDef<Claim> => !!col);
        return [...warningsColumn, ...rest];
    }, [columns, visibleColumnIds]);

    const handleRowClick = (claim: Claim) => {
        setClaimToPreview(claim);
        selectTableRowCallback(claim.claimNumber);

        chatStore.setClaimModeEnabled(true);
        chatStore.setIsClaimDataLoading(true);
        chatStore.loadClaimDetail(claim.claimNumber);
    };

    // Re-select whichever claim the sidebar's filter chip last remembered
    // (see Sidebar.tsx), once the table has loaded data to find it in. Skips
    // a persisted filter the user had cleared (chip X — isApplied: false),
    // since re-selecting it here would silently reapply a filter they turned
    // off. Runs once per mount and yields to an explicit selection (URL
    // claim_number, Sidebar's delete-chat redirect) already in flight.
    const hasAppliedPersistedClaimSelectionRef = useRef(false);

    useEffect(() => {
        if (hasAppliedPersistedClaimSelectionRef.current) {
            return;
        }
        if (claimToPreview) {
            hasAppliedPersistedClaimSelectionRef.current = true;
            return;
        }
        if (!claimsData || claimsData.length === 0) {
            return;
        }

        hasAppliedPersistedClaimSelectionRef.current = true;
        const persisted = loadPersistedClaimFilter(filtersScope);
        if (!persisted || !persisted.isApplied) {
            return;
        }

        const match = claimsData.find((claim) => claim.claimNumber === persisted.claimId);
        if (match) {
            handleRowClick(match);
        }
    }, [claimsData, claimToPreview]);

    return (
      <div
        ref={containerRef}
        style={{
          overflowX: "auto",
          background: "white",
          marginLeft: "12px",
          marginRight: "12px",
          marginBottom: "12px",
          borderRadius: "8px",
          border: "1px solid",
          borderColor: theme.palette.divider,
          // Column so the summary footer takes its height out of the rows'
          // share rather than pushing the card past the viewport. The card
          // owns the measured height; the rows get whatever the footer leaves.
          display: "flex",
          flexDirection: "column",
          height: `${tableHeight}px`,
        }}>
          <div style={{ flex: 1, minHeight: 0 }}>
            <CoreVirtualizedTable<Claim>
                isLoading={isClaimsDataLoading}
                data={claimsData || []}
                columns={visibleColumns}
                sorting={sorting}
                onSortingChange={handleSortingChange}
                height="100%"
                minWidth={500}
                onRowClick={handleRowClick}
                getRowId={(row) => row.id}
                getRowStyle={() => ({})}
                emptyMessage="No claims found"
                onLoadMore={loadMoreClaims}
                hasMore={hasMoreClaims}
                isLoadingMore={isLoadingMore}
                selectedRowId={claimToPreview?.id}
                columnFilters={columnFiltersConfig}
                disableLastOfTypeStyles
                isResizable
                pinColumns
            />
          </div>

          <ClaimsQueueSummary />
        </div>
    );
});

export default ClaimsTable;
